import {
  getMortgageMeta,
  IMortgageMetaResponseSuccess,
} from '../../api/mortgage/get_mortgage_meta';
import { getOfferType } from '../../utils/offer_type';
import { IThunkActionCreator } from '../index';
import { fetchMortgageWidgetMarkup } from '../../api/mortgage/fetch_mortgage_widget_markup';
import { timeoutPromise, TimeoutError } from '../../utils/timeout_promise';
import {
  DEFAULT_DEPOSIT_PERCENTAGES,
  DEFAULT_TERM,
} from '../../components/mortgage-calculator';

export interface IMortgageMeta {
  totalItems?: number;
  minRate?: number;
  fetching: boolean;
  mortgageWidgetMarkup?: string;
}

export interface IMortgageMetaSuccess {
  type: 'IMortgageMetaSuccess';
  payload: {
    totalItems: number;
    minRate: string;
  };
}

export interface IMortgageWidgetMarkupFetched {
  type: 'IMortgageWidgetMarkupFetched';
  payload: string;
}

export interface IMortgageMetaError {
  type: 'IMortgageMetaError';
}

export interface IMortgageMetaFetching {
  type: 'IMortgageMetaFetching';
}

export function mortgageMetaReducer(
  state: IMortgageMeta,
  action: IMortgageMetaSuccess | IMortgageMetaFetching | IMortgageMetaError | IMortgageWidgetMarkupFetched,
): IMortgageMeta {
  switch (action.type) {
    case 'IMortgageMetaSuccess':
      return {
        ...state,
        fetching: false,
        minRate: action.payload.minRate ? Number(action.payload.minRate) : state.minRate,
        totalItems: action.payload.totalItems,
      };
    case 'IMortgageMetaFetching':
      return {
        ...state,
        fetching: true,
      };
    case 'IMortgageMetaError':
      return {
        ...state,
        fetching: false,
      };
    case 'IMortgageWidgetMarkupFetched':
      return {
        ...state,
        mortgageWidgetMarkup: action.payload,
      };
    default:
      return state || {};
  }
}

export function mortgageMeta(): IThunkActionCreator {
  return (dispatch, getState, context) => {
    dispatch({ type: 'IMortgageMetaFetching' });

    const { offerData: { offer } } = getState();

    const financeApiUrl = context.config.get('financeApiUrl') as string || '';

    const amount = offer.bargainTerms.prices.rur;
    const deposit = Math.round(amount * DEFAULT_DEPOSIT_PERCENTAGES / 100);

    const regionId = offer.geo.address && offer.geo.address[0] && offer.geo.address[0].id;
    const offerType = getOfferType(offer);

    return getMortgageMeta(context.makeRequest, financeApiUrl, amount, deposit, DEFAULT_TERM, offerType, regionId)
      .then((response: IMortgageMetaResponseSuccess) => {
        dispatch({
          payload: response,
          type: 'IMortgageMetaSuccess',
        });
      })
      .catch(() => {
        dispatch({
          type: 'IMortgageMetaError',
        });
      });
  };
}

export function fetchMortgageCalculatorWidget(cookie: string): IThunkActionCreator {
  return (dispatch, getState, context) => {
    const mortgageWidgetApiUrl = context.config.get<string>('mortgageWidgetApiUrl') || '';
    const mortgageWidgetTimeout = Number(context.config.get<string>('mortgageWidget.timeout')) || 500;

    const { telemetry } = context;

    const flatPrice = getState().offerData.offer.bargainTerms.prices.rur;

    const setMortgageWidgetMarkupFetched = (payload: string) => {
      dispatch<IMortgageWidgetMarkupFetched>({
        type: 'IMortgageWidgetMarkupFetched',
        payload,
      });
    };

    return timeoutPromise(
      fetchMortgageWidgetMarkup(
        context.makeRequest,
        mortgageWidgetApiUrl,
        flatPrice,
        cookie,
      ),
      mortgageWidgetTimeout,
    )
      .catch((err) => {
        if (err instanceof TimeoutError) {
          telemetry.increment('mortgage_widget_timeout_offer');
        }

        context.logger.warning('[Degradation] Financial widget calculator', err);

        return { responseBody: '' };
      })
      .then((resp) => {
        setMortgageWidgetMarkupFetched(resp.responseBody);
      });
  };
}
